import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, type ArtistDTO, type EventDTO, type StageDTO } from "./lib/api";
import { Events } from "./views/Events";
import { Browse } from "./views/Browse";
import { Swipe } from "./views/Swipe";
import { Schedule, Conflicts } from "./views/Schedule";
import { Stages } from "./views/Stages";
import { Policy } from "./views/Policy";
import { ShareLanding } from "./views/ShareLanding";
import { ArtistSheet } from "./components/ArtistSheet";
import { InstallPrompt } from "./components/InstallPrompt";
import {
  IconBrowse,
  IconSwipe,
  IconSchedule,
  IconStages,
  IconConflict,
} from "./components/icons";
import {
  getEventPrefs,
  getStats,
  setRating as writeRating,
  setSetRating as writeSetRating,
  resetPrefs,
  type EventPrefs,
  type PreferenceMap,
  type Rating,
} from "./lib/preferences";
// Replace the whole working prefs for "Load saved schedule".
function replaceEventPrefsInStorage(eventSlug: string, next: EventPrefs): void {
  if (typeof localStorage === "undefined") return;
  const KEY = "checkthelineup:prefs:v1";
  let all: Record<string, EventPrefs> = {};
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) all = JSON.parse(raw);
  } catch { /* ignore */ }
  all[eventSlug] = {
    artists: { ...(next.artists ?? {}) },
    sets: { ...(next.sets ?? {}) },
  };
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* ignore */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("prefs:changed", { detail: { eventSlug, replaced: true } }));
  }
}
import { deriveConflicts, deriveSchedule } from "./lib/derive";
import { useRoute, type Tab } from "./lib/router";

export function App() {
  const [route, navigate] = useRoute();

  if (route.name === "events") {
    return (
      <div className="app">
        <Topbar brand="CheckTheLineUp" />
        <Events
          onPick={ev => navigate({ name: "event", eventSlug: ev.slug, tab: "browse", artistSlug: null })}
        />
        <SiteFooter navigate={navigate} />
        <InstallPrompt />
      </div>
    );
  }

  if (route.name === "policy") {
    return (
      <div className="app">
        <Policy page={route.page} onBack={() => navigate({ name: "events" })} />
        <SiteFooter navigate={navigate} />
      </div>
    );
  }

  if (route.name === "share") {
    return (
      <div className="app">
        <Topbar brand="CheckTheLineUp" />
        <ShareLanding token={route.token} navigate={navigate} />
      </div>
    );
  }

  return (
    <EventApp
      eventSlug={route.eventSlug}
      tab={route.tab}
      artistSlug={route.artistSlug}
      onBack={() => navigate({ name: "events" })}
      onChangeTab={(tab: Tab) =>
        navigate({ name: "event", eventSlug: route.eventSlug, tab, artistSlug: null })
      }
      onOpenArtist={(slug: string | null) =>
        navigate(
          { name: "event", eventSlug: route.eventSlug, tab: route.tab, artistSlug: slug },
          { replace: true },
        )
      }
    />
  );
}

function EventApp({
  eventSlug,
  tab,
  artistSlug,
  onBack,
  onChangeTab,
  onOpenArtist,
}: {
  eventSlug: string;
  tab: Tab;
  artistSlug: string | null;
  onBack: () => void;
  onChangeTab: (t: Tab) => void;
  onOpenArtist: (slug: string | null) => void;
}) {
  const [event, setEvent] = useState<EventDTO | null>(null);
  const [stages, setStages] = useState<StageDTO[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [allArtists, setAllArtists] = useState<ArtistDTO[]>([]);
  const [browseArtists, setBrowseArtists] = useState<ArtistDTO[]>([]);
  const [browseLoading, setBrowseLoading] = useState(true);
  const [browseFilter, setBrowseFilter] = useState<{
    days?: number[];
    stages?: string[];
    genres?: string[];
    q?: string;
  }>({});
  const [eventPrefs, setEventPrefs] = useState<EventPrefs>({ artists: {}, sets: {} });
  const prefs: PreferenceMap = eventPrefs.artists;

  // Initial load per event.
  useEffect(() => {
    setEvent(null);
    setAllArtists([]);
    setBrowseArtists([]);
    setBrowseLoading(true);
    setEventPrefs(getEventPrefs(eventSlug));
    Promise.all([
      api.event(eventSlug),
      api.artists(eventSlug),
      api.stages(eventSlug),
      api.genres(eventSlug),
    ])
      .then(([ev, artists, stg, gns]) => {
        setEvent(ev);
        setAllArtists(artists);
        setBrowseArtists(artists);
        setStages(stg);
        setGenres(gns);
        setBrowseLoading(false);
      })
      .catch(err => {
        console.error(err);
        setBrowseLoading(false);
      });
  }, [eventSlug]);

  // Reload browse when filter changes.
  useEffect(() => {
    if (!event) return;
    setBrowseLoading(true);
    api.artists(eventSlug, browseFilter).then(a => {
      setBrowseArtists(a);
      setBrowseLoading(false);
    });
  }, [eventSlug, event, browseFilter]);

  // Refresh prefs when any component updates them.
  useEffect(() => {
    const refresh = () => setEventPrefs(getEventPrefs(eventSlug));
    window.addEventListener("prefs:changed", refresh);
    return () => window.removeEventListener("prefs:changed", refresh);
  }, [eventSlug]);

  const rate = useCallback(
    (artistSlug: string, rating: Rating | null) => {
      writeRating(eventSlug, artistSlug, rating);
    },
    [eventSlug],
  );

  const rateSet = useCallback(
    (setId: number, rating: Rating | null) => {
      writeSetRating(eventSlug, setId, rating);
    },
    [eventSlug],
  );

  const reset = useCallback(() => {
    resetPrefs(eventSlug);
  }, [eventSlug]);

  const importPrefs = useCallback(
    (next: PreferenceMap) => {
      for (const [slug, r] of Object.entries(next)) {
        writeRating(eventSlug, slug, r);
      }
    },
    [eventSlug],
  );

  const schedule = useMemo(
    () => deriveSchedule(allArtists, prefs, stages, { setPrefs: eventPrefs.sets }),
    [allArtists, prefs, stages, eventPrefs.sets],
  );
  const conflicts = useMemo(
    () => deriveConflicts(allArtists, prefs, { setPrefs: eventPrefs.sets }),
    [allArtists, prefs, eventPrefs.sets],
  );
  const stats = event ? getStats(eventSlug, event.artistCount) : null;

  const onChangeBrowseFilter = useCallback(
    (f: typeof browseFilter) => setBrowseFilter(f),
    [],
  );

  // Events whose 2026 lineup hasn't been released yet (no artists seeded,
  // no sets). Render a dedicated TBA view in place of the tabs — swiping
  // nothing / browsing nothing / building a schedule out of nothing all
  // look broken. The moment the real lineup drops, this branch goes away.
  if (event && event.artistCount === 0) {
    return (
      <div className="app">
        <Topbar
          brand="CheckTheLineUp"
          eventName={event.name}
          onBack={onBack}
        />
        <LineupTBA event={event} />
      </div>
    );
  }

  return (
    <div className="app">
      <Topbar
        brand="CheckTheLineUp"
        eventName={event?.name}
        onBack={onBack}
        stats={stats}
      />

      {tab === "browse" && (
        <Browse
          stages={stages}
          genres={genres}
          artists={browseArtists}
          loading={browseLoading}
          prefs={prefs}
          eventName={event?.name ?? null}
          eventLocation={event?.location ?? null}
          eventStartDate={event?.startDate ?? null}
          eventEndDate={event?.endDate ?? null}
          onOpen={onOpenArtist}
          onChangeFilter={onChangeBrowseFilter}
        />
      )}
      {tab === "swipe" && <Swipe artists={allArtists} prefs={prefs} onRate={rate} />}
      {tab === "schedule" && (
        <Schedule
          schedule={schedule}
          conflicts={conflicts}
          event={event}
          prefs={prefs}
          eventPrefs={eventPrefs}
          onOpen={onOpenArtist}
          onReset={reset}
          onImport={importPrefs}
          onLoadSchedule={next => replaceEventPrefsInStorage(eventSlug, next)}
        />
      )}
      {tab === "stages" && (
        <Stages
          stages={stages}
          artists={allArtists}
          onPickStage={slug => {
            onChangeTab("browse");
            setBrowseFilter({ stages: [slug] });
          }}
        />
      )}
      {tab === "clash" && (
        <Conflicts
          conflicts={conflicts}
          artists={allArtists}
          onOpen={onOpenArtist}
          onRate={rate}
        />
      )}

      {artistSlug && (
        <ArtistSheet
          eventSlug={eventSlug}
          eventStartDate={event?.startDate ?? null}
          slug={artistSlug}
          rating={prefs[artistSlug] ?? null}
          setRatings={eventPrefs.sets}
          onClose={() => onOpenArtist(null)}
          onRate={rate}
          onRateSet={rateSet}
        />
      )}

      <Tabbar tab={tab} setTab={onChangeTab} conflictCount={conflicts.length} />
    </div>
  );
}

function LineupTBA({ event }: { event: EventDTO }) {
  const dateRange = useMemo(() => {
    const fmt = (iso: string) => {
      const [, mm, dd] = iso.split("-");
      const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${M[parseInt(mm!, 10) - 1]} ${parseInt(dd!, 10)}`;
    };
    return event.startDate === event.endDate
      ? fmt(event.startDate)
      : `${fmt(event.startDate)}–${fmt(event.endDate)}, ${event.year}`;
  }, [event.startDate, event.endDate, event.year]);

  return (
    <div className="lineup-tba" data-testid="lineup-tba">
      <span className="super">LINEUP TBA</span>
      <h2>{event.name}</h2>
      <div className="lineup-tba-meta">
        {dateRange}
        {event.location ? ` · ${event.location}` : ""}
      </div>
      <div className="lineup-tba-card">
        <div className="emoji" aria-hidden="true">🎟️</div>
        <p>
          The 2026 lineup hasn't been released yet. As soon as the festival
          posts its artists we'll drop them in so you can start swiping and
          building your schedule.
        </p>
        <p className="lineup-tba-hint">
          Come back closer to {dateRange} — or follow the festival's own
          channels for announcement timing.
        </p>
      </div>
    </div>
  );
}

function Topbar({
  brand,
  eventName,
  onBack,
  stats,
}: {
  brand: string;
  eventName?: string;
  onBack?: () => void;
  stats?: { rated: number; total: number; like: number; superlike: number } | null;
}) {
  return (
    <div className="topbar">
      <div className="brand">
        {onBack ? (
          <button
            onClick={onBack}
            aria-label="Back to events"
            data-testid="back-to-events"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 8px 4px 4px",
              border: "1px solid var(--panel-edge)",
              background: "var(--bg-2)",
              borderRadius: 999,
              color: "var(--ink)",
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            <span style={{ fontSize: 14 }}>‹</span> {eventName ?? "events"}
          </button>
        ) : (
          <>
            <span className="brand-mark" />
            <span>{brand}</span>
          </>
        )}
      </div>
      {stats && (
        <div className="stats">
          <span className="lcd amber" data-testid="stat-likes">♥ {stats.like}</span>
          <span className="lcd" data-testid="stat-super">★ {stats.superlike}</span>
          <span className="lcd" data-testid="stat-total">{stats.rated}/{stats.total}</span>
        </div>
      )}
    </div>
  );
}

function SiteFooter({
  navigate,
}: {
  navigate: (r: import("./lib/router").Route, opts?: { replace?: boolean }) => void;
}) {
  const go = (
    e: React.MouseEvent<HTMLAnchorElement>,
    page: "privacy" | "terms" | "disclosure" | "about" | "support",
  ) => {
    e.preventDefault();
    navigate({ name: "policy", page });
  };
  return (
    <footer className="site-footer" data-testid="site-footer">
      <nav aria-label="Site footer">
        <a href="/about" onClick={e => go(e, "about")}>About</a>
        <span aria-hidden="true">·</span>
        <a href="/privacy" onClick={e => go(e, "privacy")}>Privacy</a>
        <span aria-hidden="true">·</span>
        <a href="/terms" onClick={e => go(e, "terms")}>Terms</a>
        <span aria-hidden="true">·</span>
        <a href="/disclosure" onClick={e => go(e, "disclosure")}>Disclosure</a>
        <span aria-hidden="true">·</span>
        <a href="/support" onClick={e => go(e, "support")}>Support</a>
        <span aria-hidden="true">·</span>
        <a
          href="https://instagram.com/yocheckthelineup"
          target="_blank"
          rel="noopener noreferrer me"
        >@yocheckthelineup</a>
      </nav>
      <div className="site-footer-small">
        © {new Date().getFullYear()} CheckTheLineUp · Independent project,
        not affiliated with any festival organiser or featured artist.
      </div>
    </footer>
  );
}

function Tabbar({ tab, setTab, conflictCount }: { tab: Tab; setTab: (t: Tab) => void; conflictCount: number }) {
  return (
    <nav className="tabbar" aria-label="Primary">
      <button aria-current={tab === "browse" ? "page" : undefined} onClick={() => setTab("browse")} data-testid="tab-browse">
        <span className="tab-dot" /><IconBrowse /><span>Browse</span>
      </button>
      <button aria-current={tab === "swipe" ? "page" : undefined} onClick={() => setTab("swipe")} data-testid="tab-swipe">
        <span className="tab-dot" /><IconSwipe /><span>Swipe</span>
      </button>
      <button aria-current={tab === "schedule" ? "page" : undefined} onClick={() => setTab("schedule")} data-testid="tab-schedule">
        <span className="tab-dot" /><IconSchedule /><span>My Lineup</span>
      </button>
      <button aria-current={tab === "stages" ? "page" : undefined} onClick={() => setTab("stages")} data-testid="tab-stages">
        <span className="tab-dot" /><IconStages /><span>Stages</span>
      </button>
      <button aria-current={tab === "clash" ? "page" : undefined} onClick={() => setTab("clash")} data-testid="tab-clash" style={{ position: "relative" }}>
        <span className="tab-dot" /><IconConflict /><span>Clash</span>
        {conflictCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 4, right: 8,
              width: 16, height: 16, borderRadius: 8,
              background: "var(--red)",
              color: "#fff",
              fontSize: 9, fontWeight: 700,
              display: "grid", placeItems: "center",
            }}
          >{conflictCount}</span>
        )}
      </button>
    </nav>
  );
}
